import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Lock } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { getNda, acceptNda } from "@/lib/api";

export const NdaModal = () => {
  const { user, refreshUsage, logout } = useAuth();
  const [nda, setNda] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scrolledEnd, setScrolledEnd] = useState(false);

  const open = !!user && user.nda_accepted === false;

  useEffect(() => {
    if (open && !nda) getNda().then(setNda).catch(() => setNda(null));
  }, [open, nda]);

  const agree = async () => {
    setBusy(true);
    try {
      await acceptNda();
      await refreshUsage();
      toast.success("Confidentiality agreement accepted.");
    } catch {
      toast.error("Could not record acceptance. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-2xl gap-0 p-0 [&>button]:hidden"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="nda-modal"
      >
        <DialogHeader className="border-b border-border p-6 pb-4">
          <DialogTitle className="flex items-center gap-2 font-display text-xl font-black tracking-tight">
            <ShieldCheck className="h-5 w-5 text-primary" />
            {nda?.title || "Confidentiality & Non-Disclosure Agreement"}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Please review and accept before preparing any estimate. Your data stays private to your account.
          </DialogDescription>
        </DialogHeader>

        <div
          data-testid="nda-body"
          onScroll={(e) => {
            const el = e.currentTarget;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setScrolledEnd(true);
          }}
          className="max-h-[46vh] overflow-y-auto whitespace-pre-line px-6 py-4 text-sm leading-relaxed text-foreground/90"
        >
          {nda ? nda.body : "Loading agreement…"}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border p-5">
          <button
            data-testid="nda-decline"
            onClick={logout}
            className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-destructive"
          >
            Decline &amp; sign out
          </button>
          <Button
            data-testid="nda-agree"
            onClick={agree}
            disabled={busy || !nda || !scrolledEnd}
            className="gap-2 rounded-sm"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            {scrolledEnd ? "I Agree" : "Scroll to read, then Agree"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
